import React, { useState, useEffect, useRef } from 'react';
import Sideboard from './Sideboard';
import MainContent from './MainContent';
import Dashboard from './Dashboard';
import { useAuth } from '../Contexts/AuthContext';
import SettingsPage from './SettingsPage';
import TokenChecker, { WS_BASE_URL } from './TokenChecker';
import FlowsynSetup from './Flowsync/FlowsyncSetup';

import { log, warn, error as logError } from "../utils/logger";

export default function Main() {
  const [currCategory, setCurrCategory] = useState("Personal Tasks");
  const [activeCategory, setActiveCategory] = useState("")
  const [isPersonalActive, setIsPersonalActive] = useState("Personal Tasks")
  const [addTaskSignal, setAddTaskSignal] = useState(0)
  
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { authData } = useAuth();
  const socketRef = useRef(null);
  const audioRef = useRef(null);
  const soundPrefRef = useRef(true);

  // Preload notification sound
  useEffect(() => {
    try {
      const audio = new Audio((process.env.PUBLIC_URL || '') + '/FlowmanSound.wav');
      audio.preload = 'auto';
      audio.volume = 0.6; // tweak as needed
      audioRef.current = audio;
    } catch (_) {
      // ignore audio init errors (e.g., SSR or unsupported env)
    }
  }, []);

  // Init and sync sound preference (same-tab + cross-tab)
  useEffect(() => {
    const readPref = () => {
      try {
        const v = localStorage.getItem('notificationSoundEnabled');
        soundPrefRef.current = (v === null) ? true : (v === 'true');
      } catch (_) { soundPrefRef.current = true; }
    };
    readPref();
    const onStorage = (e) => {
      if (e.key === 'notificationSoundEnabled') readPref();
    };
    const onCustom = (e) => {
      if (e?.detail && typeof e.detail.enabled === 'boolean') {
        soundPrefRef.current = e.detail.enabled;
        if (!e.detail.enabled) {
          try {
            const a = audioRef.current;
            if (a) { a.pause(); a.currentTime = 0; }
          } catch (_) {}
        }
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('flowman:notification-sound', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('flowman:notification-sound', onCustom);
    };
  }, []);

  const playNotificationSound = () => {
    // Respect up-to-date preference (default: true)
    if (!soundPrefRef.current) return;
    const audio = audioRef.current;
    if (!audio) return;
    try {
      // restart from beginning and play; ignore autoplay restrictions quietly
      audio.currentTime = 0;
      const p = audio.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {});
      }
    } catch (_) {
      // no-op if browser blocks or errors
    }
  };

  const handleCategorySelect = (category, action) => {
    setCurrCategory(category);
    setActiveCategory(category);
    setIsPersonalActive(category);
    if (action === 'create-task') {
      setAddTaskSignal((s) => s + 1);
    }
  };

  // Listen to cross-tab accept message
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'TASK_ACCEPTED') {
        log('Task accepted:', event.data.taskId);
        window.location.href = '/home';
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!authData?.accessToken) return;
      try {
        const res = await TokenChecker('/notifications/', { method: 'GET' });
        if (res.ok) {
          const data = await res.json();
          const list = (data.notifications || []).filter(n => !n.is_deleted);
          const unread = list.filter(n => !n.is_read);
          setNotifications(list);
          setUnreadCount(unread.length);
          log("📋 Loaded existing notifications:", list.length);
        } else {
          warn('Failed to load notifications:', res.status);
        }
      } catch (error) {
        logError("❌ Failed to fetch notifications:", error);
      }
    };

    fetchNotifications();
  }, [authData]);

  // 🌐 WebSocket connection for notifications
  useEffect(() => {
    if (!authData?.accessToken) return;

    let reconnectTimeout = null;
    const attemptsRef = { current: 0 };

    const connect = () => {
      const wsUrl = `${WS_BASE_URL}/ws/notifications/`;
      // Send JWT as a subprotocol to avoid putting it in the URL
      const ws = new WebSocket(wsUrl, ['jwt', authData.accessToken]);
      socketRef.current = ws;

      ws.onopen = () => {
        attemptsRef.current = 0;
        log("✅ Notification WS connected");
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        log("🔔 Notification received");
        setNotifications((prev) => {
          const exists = prev.some((n) => n.id === data.id);
          if (exists) return prev;
          // increment unread on new arrival
          setUnreadCount((u) => u + 1);
          // play sound for brand-new notifications
          playNotificationSound();
          return [data, ...prev];
        });
      };

      ws.onclose = (event) => {
        log("❌ Notification WS disconnected", event.code, event.reason);
        // simple backoff reconnect while authenticated
        if (authData?.accessToken) {
          const delay = Math.min(30000, 1000 * Math.pow(2, attemptsRef.current++));
          reconnectTimeout = setTimeout(connect, delay);
        }
      };

      ws.onerror = (error) => {
        logError("🚨 WebSocket error:", error);
        try { ws.close(); } catch (_) {}
      };
    };

    connect();

    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      const ws = socketRef.current;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close();
      }
    };
  }, [authData]);

  return (
    <div className="flex h-screen bg-[#F7FAFC] overflow-hidden">
      <div className="flex-shrink-0">
        <Sideboard
          onCategorySelect={handleCategorySelect}
          activeCategory={activeCategory}
          personalActive={isPersonalActive}
        />
      </div>

      {currCategory === "Dashboard" ? (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <Dashboard onCategorySelect={handleCategorySelect} />
        </div>
      ) : currCategory === "Settings" ? (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <SettingsPage />
        </div>
      ) : currCategory === "Flowsync" ?(
        <div className="flex-1 min-h-0 overflow-y-auto">
          <FlowsynSetup />
        </div>
      ): (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <MainContent
            category={currCategory}
            notifications={notifications}
            unreadCount={unreadCount}
            setNotifications={setNotifications}
            setUnreadCount={setUnreadCount}
            addTaskSignal={addTaskSignal}
          />
        </div>
      )}
    </div>
  );
}
